import React, { useState, useEffect, useRef } from 'react';

interface GlitchTextProps {
  text: string;
  fontSize?: string;
  className?: string;
  intensity?: 'low' | 'medium' | 'high'; // Glitch intensity
  color?: string;
  highlight?: boolean; // Enable highlight effect
  scanline?: boolean; // Add scanline effect
  animated?: boolean; // Continuous animation or just on hover
  onHover?: () => void;
}

const GlitchText: React.FC<GlitchTextProps> = ({
  text,
  fontSize = '24px',
  className = '',
  intensity = 'medium',
  color = '#00ff00',
  highlight = false,
  scanline = false, 
  animated = true,
  onHover
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [glitchText, setGlitchText] = useState(text);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  
  // Characters to use for glitch effect
  const glitchChars = '!<>-_\\/[]{}—=+*^?#________';
  
  // Set frequency of glitches based on intensity
  const getGlitchFrequency = () => {
    switch (intensity) {
      case 'low': return 0.02;
      case 'high': return 0.2;
      default: return 0.08; // medium
    }
  };
  
  // Generate a glitched version of the text
  const createGlitchedText = () => {
    const frequency = getGlitchFrequency() * (isHovered ? 2 : 1);
    
    let result = '';
    for (let i = 0; i < text.length; i++) {
      // Randomly replace characters with glitch characters
      if (Math.random() < frequency) {
        const glitchChar = glitchChars[Math.floor(Math.random() * glitchChars.length)];
        result += glitchChar;
      } else {
        result += text[i];
      }
    }
    
    return result;
  };
  
  // Create glitch displacement effect
  const createGlitchDisplacement = () => {
    if (isHovered || animated) {
      const frequency = getGlitchFrequency() * (isHovered ? 3 : 1);
      
      if (Math.random() < frequency) {
        // Random x/y offset for the text position
        setOffset({
          x: (Math.random() - 0.5) * 10,
          y: (Math.random() - 0.5) * 4
        });
      } else {
        setOffset({ x: 0, y: 0 });
      }
    }
  };
  
  // Animation loop for continuous glitch effect
  const animate = () => {
    if (animated || isHovered) {
      setGlitchText(createGlitchedText());
      createGlitchDisplacement();
    }
    
    animationRef.current = requestAnimationFrame(animate);
  };
  
  // Start/stop animation based on props and hover state
  useEffect(() => {
    if (animated || isHovered) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      setGlitchText(text);
      setOffset({ x: 0, y: 0 });
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animated, isHovered, text]);
  
  // Create RGB split effect style
  const getRgbSplitStyle = () => {
    if (!isHovered && !animated) return {};
    
    const intensity = isHovered ? 3 : 1;
    
    return {
      textShadow: `
        ${Math.random() < 0.5 ? 0.5 : 1.5}px 0 ${Math.random() < 0.5 ? 0.5 : 1}px rgba(255,0,0,0.7),
        ${Math.random() > 0.5 ? -0.5 : -1.5}px 0 ${Math.random() > 0.5 ? 0.5 : 1}px rgba(0,255,255,0.7),
        0 0 3px
      `
    };
  };
  
  // Handle mouse enter event
  const handleMouseEnter = () => {
    setIsHovered(true);
    if (onHover) onHover();
  };
  
  // Handle mouse leave event
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  
  // Create style object
  const style: React.CSSProperties = {
    position: 'relative',
    fontSize,
    color,
    fontFamily: "'VT323', monospace",
    display: 'inline-block',
    transform: `translate(${offset.x}px, ${offset.y}px)`,
    transition: 'transform 0.05s linear',
    ...getRgbSplitStyle()
  };
  
  // Add highlight effect to the style
  if (highlight) {
    style.textShadow = `${style.textShadow || ''}, 0 0 5px ${color}, 0 0 10px ${color}`;
  }
  
  return (
    <div
      className={`glitch-text ${className}`}
      style={style}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={containerRef}
    >
      {glitchText}
      
      {/* Scanline effect */}
      {scanline && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: 0,
          width: '100%',
          height: '1px',
          backgroundColor: 'rgba(0, 255, 0, 0.7)',
          boxShadow: '0 0 2px rgba(0, 255, 0, 0.5)',
          animation: 'scanline 2s linear infinite',
          zIndex: 1,
          pointerEvents: 'none'
        }} />
      )}
      
      {/* Add subtle glitch clip effect on hover */}
      {isHovered && (
        <>
          <div style={{
            content: '',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: color,
            opacity: Math.random() < 0.1 ? 0.8 : 0,
            zIndex: 1,
            animation: 'glitch-flash 0.5s linear infinite',
            pointerEvents: 'none'
          }} />
          <div style={{
            position: 'absolute',
            top: 0,
            left: 3,
            width: '98%',
            height: '100%',
            color: 'rgba(255, 255, 255, 0.8)',
            textShadow: 'none',
            clipPath: 'polygon(0 0, 100% 0, 100% 25%, 0 25%, 0 30%, 100% 30%, 100% 50%, 0 50%, 0 60%, 100% 60%, 100% 75%, 0 75%, 0 80%, 100% 80%, 100% 100%, 0 100%)',
            animation: 'glitch-text 0.25s linear infinite',
            opacity: Math.random() < 0.1 ? 1 : 0,
            pointerEvents: 'none'
          }}>{glitchText}</div>
        </>
      )}
    </div>
  );
};

export default GlitchText;